'use client';

import React from 'react';
import type { LucideProps } from 'lucide-react';
import { InputPrefixSuffixCds } from '@/components/cds/input-prefix-suffix-cds';
import { IconClientRendererCdsType } from '@/components/cds/icon-client-renderer-cds';

/**
 * Props for the InputIconCds component.
 *
 * @interface InputIconCdsProps
 * @extends {React.InputHTMLAttributes<HTMLInputElement>}
 *
 * @property {LucideIcon} [prefixIcon] - Optional icon to be displayed at the beginning of the input field.
 * @property {LucideIcon} [suffixIcon] - Optional icon to be displayed at the end of the input field.
 * @property {Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>} [prefixIconProps] - Additional properties for the prefix icon.
 * @property {Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>} [suffixIconProps] - Additional properties for the suffix icon.
 * @property {string} [prefixClassName] - Optional class name to apply to the prefix icon.
 * @property {string} [suffixClassName] - Optional class name to apply to the suffix icon.
 * @property {string} [iconClassName] - Optional class name to apply to both icons.
 * @property {() => void} [onPrefixClick] - Optional callback function to handle prefix icon click events.
 * @property {() => void} [onSuffixClick] - Optional callback function to handle suffix icon click events.
 */
interface InputIconCdsProps extends React.InputHTMLAttributes<HTMLInputElement> {
  prefixIcon?: IconClientRendererCdsType;
  suffixIcon?: IconClientRendererCdsType;
  prefixIconProps?: Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>;
  suffixIconProps?: Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>;
  prefixClassName?: string;
  suffixClassName?: string;
  iconClassName?: string;
  onPrefixClick?: () => void;
  onSuffixClick?: () => void;
}

/**
 * `InputIconCds` is a React functional component that renders an input field with optional prefix and suffix icons.
 * It uses `React.forwardRef` to forward the ref to the underlying input element.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} [props.className] - Additional class names to apply to the input element.
 * @param {React.ElementType} [props.prefixIcon] - An optional icon component to render as a prefix inside the input.
 * @param {React.ElementType} [props.suffixIcon] - An optional icon component to render as a suffix inside the input.
 * @param {Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>} [props.prefixIconProps] - Additional properties for the prefix icon component.
 * @param {Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>} [props.suffixIconProps] - Additional properties for the suffix icon component.
 * @param {string} [props.prefixClassName] - Additional class names to apply to the prefix icon component.
 * @param {string} [props.suffixClassName] - Additional class names to apply to the suffix icon component.
 * @param {string} [props.iconClassName] - Additional class names to apply to both icon components.
 * @param {React.Ref<HTMLInputElement>} ref - The ref to be forwarded to the input element.
 *
 * @returns {JSX.Element} The rendered input field with optional prefix and suffix icons.
 */
const InputIconCds = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'> & InputIconCdsProps
>(({ iconClassName, ...props }, ref) => {
  return <InputPrefixSuffixCds {...props} prefixSuffixClassName={iconClassName} ref={ref} />;
});
InputIconCds.displayName = 'InputIconCds';

export { InputIconCds };
export type { InputIconCdsProps };
